import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../../util/types';
import {
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../../util/fieldHelpers';
import { azureTranslate, saveListingTranslation } from '../../../util/api';
import { translateTextsInBatches } from '../../../util/azureTranslateHelper';

export const EXPERIENCE_DETAILS_FIELD_KEYS = [
  'listingFieldID_program',
  'field_itinerary',
  'field_included',
  'field_whattobring',
];

export const PROGRAM_FIELD_KEY = 'listingFieldID_program';
export const LEGACY_ITINERARY_FIELD_KEY = 'field_itinerary';

export const hasSavedProgramDay = publicData =>
  publicData != null && Object.prototype.hasOwnProperty.call(publicData, 'programDay');

export const shouldShowLegacyItinerary = (publicData, legacyItineraryValue) =>
  !!legacyItineraryValue && !hasSavedProgramDay(publicData);

export const filterExperienceDetailsFieldsForForm = listingFields =>
  filterExperienceDetailsFields(listingFields).filter(
    fieldConfig => fieldConfig.key !== LEGACY_ITINERARY_FIELD_KEY
  );

export const getProgramFieldConfig = listingFields =>
  listingFields?.find(fieldConfig => fieldConfig.key === PROGRAM_FIELD_KEY);

export const isProgramFieldApplicable = (listingType, categories, listingFields) => {
  const programFieldConfig = getProgramFieldConfig(listingFields);
  if (!programFieldConfig) {
    return false;
  }

  const categoryIds = Array.isArray(categories)
    ? categories
    : Object.values(categories || {});

  return (
    isFieldForListingType(listingType, programFieldConfig) &&
    isFieldForCategory(categoryIds, programFieldConfig)
  );
};

export const EXPERIENCE_DETAILS = 'experience-details';

export const isExperienceDetailsField = key => EXPERIENCE_DETAILS_FIELD_KEYS.includes(key);

export const filterExperienceDetailsFields = listingFields =>
  listingFields.filter(fieldConfig => EXPERIENCE_DETAILS_FIELD_KEYS.includes(fieldConfig.key));

export const excludeExperienceDetailsFields = listingFields =>
  listingFields.filter(fieldConfig => !EXPERIENCE_DETAILS_FIELD_KEYS.includes(fieldConfig.key));

export const isExperienceDetailsTabVisible = (listing, listingTypesIds, selectedListingType) => {
  const listingType =
    listing?.attributes?.publicData?.listingType || selectedListingType?.listingType;
  const categoryLevel1 = listing?.attributes?.publicData?.categoryLevel1;

  if (categoryLevel1 === 'category-tours' || categoryLevel1 === 'category-experiences') {
    return true;
  }

  if (listingType && listingTypesIds) {
    return (
      listingTypesIds['category-tours']?.includes(listingType) ||
      listingTypesIds['category-experiences']?.includes(listingType)
    );
  }

  return false;
};

export const buildLocalizedFieldsFromTranslation = (translated, originalTexts) => {
  const localizedFields = {};

  if (!translated) {
    return localizedFields;
  }

  for (const key in translated) {
    const translationsForKey = translated[key];

    Object.entries(translationsForKey).forEach(([lang, text]) => {
      localizedFields[`${key}_locale_${lang}`] = text;
    });
  }

  Object.keys(originalTexts).forEach(key => {
    if (!originalTexts[key]) {
      localizedFields[key] = null;
    }
  });

  return localizedFields;
};

export const extractTextsFromProgramDay = programDay => {
  const texts = {};
  const textMap = [];

  if (programDay && Array.isArray(programDay)) {
    programDay.forEach((day, dayIndex) => {
      if (day.schedules && Array.isArray(day.schedules)) {
        day.schedules.forEach((schedule, scheduleIndex) => {
          if (schedule.description) {
            const key = `pd_${dayIndex}_${scheduleIndex}_desc`;
            texts[key] = schedule.description;
            textMap.push({
              type: 'programDay',
              dayIndex,
              scheduleIndex,
              field: 'description',
              key,
            });
          }
        });
      }
    });
  }

  return { texts, textMap };
};

export const restoreTranslatedProgramDay = (programDay, translated, textMap, languages) => {
  const result = {
    programDay: programDay ? JSON.parse(JSON.stringify(programDay)) : null,
  };

  if (!translated) {
    return result;
  }

  languages.forEach(lang => {
    textMap.forEach(item => {
      const translatedText = translated[item.key]?.[lang];
      if (!translatedText) {
        return;
      }

      if (item.type === 'programDay' && result.programDay) {
        if (result.programDay[item.dayIndex]?.schedules?.[item.scheduleIndex]) {
          result.programDay[item.dayIndex].schedules[item.scheduleIndex][item.field] = translatedText;
        }
      }
    });

    if (result.programDay) {
      result[`programDay_locale_${lang}`] = JSON.stringify(result.programDay);
    }
  });

  return result;
};

export const translateAndSaveExperienceFieldsSequentially = async ({
  listingId,
  fieldsToTranslate,
  clearedFieldKeys,
  languages,
  programDay,
}) => {
  if (!listingId) {
    return;
  }

  for (const key of clearedFieldKeys) {
    await saveListingTranslation({
      listingId,
      localizedFields: { [key]: null },
    });
  }

  for (const { key, text } of fieldsToTranslate) {
    let translated = null;

    try {
      translated = await translateTextsInBatches(
        azureTranslate,
        { [key]: text },
        languages,
        { batchLabel: key }
      );
    } catch (error) {
      console.error(`Translation failed for ${key}`, error);
    }

    const localizedFields = buildLocalizedFieldsFromTranslation(translated, { [key]: text });

    if (Object.keys(localizedFields).length > 0) {
      await saveListingTranslation({
        listingId,
        localizedFields,
      });
    }
  }

  const hasProgramDay = programDay?.[0]?.schedules?.length;
  if (!hasProgramDay) {
    const clearedProgramDayLocales = languages.reduce((acc, lang) => {
      acc[`programDay_locale_${lang}`] = null;
      return acc;
    }, {});

    await saveListingTranslation({
      listingId,
      localizedFields: clearedProgramDayLocales,
    });
    return;
  }

  const { texts: programDayTexts, textMap } = extractTextsFromProgramDay(programDay);
  if (Object.keys(programDayTexts).length === 0) {
    return;
  }

  let translatedProgramDay = null;
  try {
    translatedProgramDay = await translateTextsInBatches(
      azureTranslate,
      programDayTexts,
      languages,
      { batchLabel: 'programDay' }
    );
  } catch (error) {
    console.error('Translation failed for programDay', error);
  }

  const translatedArrays = restoreTranslatedProgramDay(
    programDay,
    translatedProgramDay,
    textMap,
    languages
  );
  const programDayLocalizedFields = {};
  Object.keys(translatedArrays).forEach(key => {
    if (key.includes('_locale_')) {
      programDayLocalizedFields[key] = translatedArrays[key];
    }
  });

  if (Object.keys(programDayLocalizedFields).length > 0) {
    await saveListingTranslation({
      listingId,
      localizedFields: programDayLocalizedFields,
    });
  }
};

export const hasValidExperienceDetailsFields = (publicData, privateData, config) => {
  const categoryKey = config.categoryConfiguration.key;
  const categoryOptions = config.categoryConfiguration.categories;
  const categoriesObj = pickCategoryFields(publicData, categoryKey, 1, categoryOptions);
  const experienceFieldConfigs = filterExperienceDetailsFields(config.listing.listingFields);

  if (experienceFieldConfigs.length === 0) {
    return true;
  }

  const currentCategories = Object.values(categoriesObj);

  const isValidField = (fieldConfig, fieldData) => {
    const { key, schemaType, enumOptions = [], saveConfig = {} } = fieldConfig;

    const schemaOptionKeys = enumOptions.map(o => `${o.option}`);
    const hasValidEnumValue = optionData => schemaOptionKeys.includes(optionData);
    const hasValidMultiEnumValues = savedOptions =>
      savedOptions.every(optionData => schemaOptionKeys.includes(optionData));

    const isTargetListingType = isFieldForListingType(publicData?.listingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    const isRequired = !!saveConfig.isRequired && isTargetListingType && isTargetCategory;

    if (!isTargetListingType || !isTargetCategory) {
      return true;
    }

    if (isRequired) {
      const savedListingField = fieldData[key];
      return schemaType === SCHEMA_TYPE_ENUM
        ? typeof savedListingField === 'string' && hasValidEnumValue(savedListingField)
        : schemaType === SCHEMA_TYPE_MULTI_ENUM
        ? Array.isArray(savedListingField) && hasValidMultiEnumValues(savedListingField)
        : schemaType === SCHEMA_TYPE_TEXT
        ? typeof savedListingField === 'string'
        : schemaType === SCHEMA_TYPE_LONG
        ? typeof savedListingField === 'number' && Number.isInteger(savedListingField)
        : schemaType === SCHEMA_TYPE_BOOLEAN
        ? savedListingField === true || savedListingField === false
        : schemaType === SCHEMA_TYPE_YOUTUBE
        ? typeof savedListingField === 'string'
        : false;
    }

    return true;
  };

  return experienceFieldConfigs.reduce((isValid, fieldConfig) => {
    const data = fieldConfig.scope === 'private' ? privateData : publicData;
    return isValid && isValidField(fieldConfig, data);
  }, true);
};
